import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AppProvider } from '@shopify/polaris';
import { Provider as AppBridgeProvider } from '@shopify/app-bridge-react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ConfigProvider } from './contexts/ConfigContext';
import '@shopify/polaris/build/esm/styles.css';

// Pages
import Dashboard from './pages/Dashboard';
import ExportOrders from './pages/ExportOrders';
import Configuration from './pages/Configuration';
import History from './pages/History';
import SyncDashboard from './pages/SyncDashboard';
import Pricing from './pages/Pricing';
import Billing from './pages/Billing';
import AuthCallback from './pages/AuthCallback';
import AuthError from './pages/AuthError';
import Landing from './pages/Landing';
import Install from './pages/Install';
import Loading from './components/Loading';
import { startShopifyInstall } from './utils/shopifyInstall';

// Layout
import AppLayout from './components/AppLayout';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Read embedded-mode params once at boot
const initialParams = new URLSearchParams(window.location.search);
const embedHost = initialParams.get('host') || '';
// This is a STANDALONE app (shopify.app.toml embedded=false) authenticated with
// a JWT kept in localStorage. It must run at the top level, NOT inside the
// Shopify admin iframe: third-party storage is partitioned there, so the JWT
// never persists and the app loops through OAuth forever. So never engage App
// Bridge's forceRedirect embedding — keep isEmbedded false even when Shopify
// passes a `host` param.
const isEmbedded = false;
void embedHost;

// Guard so the OAuth redirect is attempted at most once per page load. The route
// guards below call this during render; without the flag it would re-fire on
// every re-render (e.g. when a context finishes loading), spamming
// /api/shopify/auth and tripping the backend rate limiter.
let installRedirectStarted = false;
const beginInstallOnce = (shop: string) => {
  if (installRedirectStarted) return;
  installRedirectStarted = true;
  startShopifyInstall(shop).catch((err) => {
    installRedirectStarted = false;
    console.error('Failed to start Shopify install:', err);
  });
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loading />;
  }

  if (!isAuthenticated) {
    const shopParam = new URLSearchParams(window.location.search).get('shop');

    if (shopParam) {
      beginInstallOnce(shopParam);
      return <Loading message="Redirecting to Shopify authentication..." />;
    }

    // Send prospects to the public landing page
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Redirect authenticated users away from the public landing page
const HomeRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <Loading />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Opened from the Shopify admin (a `shop` param is present) but no session yet
  // — start OAuth so the merchant lands straight in the dashboard rather than the
  // public landing page. Runs top-level, so the JWT persists and this happens
  // once, not in a loop.
  const shopParam = new URLSearchParams(window.location.search).get('shop');
  if (shopParam) {
    beginInstallOnce(shopParam);
    return <Loading message="Redirecting to Shopify authentication..." />;
  }

  return <Landing />;
};

const ShopifyProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!isEmbedded) {
    // Standalone (public) mode — App Bridge requires a host param it doesn't have here
    return <>{children}</>;
  }

  const appBridgeConfig = {
    apiKey: process.env.REACT_APP_SHOPIFY_API_KEY || '',
    host: embedHost,
    forceRedirect: true,
  };

  return <AppBridgeProvider config={appBridgeConfig}>{children}</AppBridgeProvider>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppProvider i18n={{}}>
        <ShopifyProviders>
          <AuthProvider>
            <ConfigProvider>
              <Router>
                <div className="App">
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<HomeRoute />} />
                    <Route path="/install" element={<Install />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/auth/error" element={<AuthError />} />

                    {/* Protected routes */}
                    <Route path="/dashboard" element={
                      <ProtectedRoute>
                        <AppLayout>
                          <Dashboard />
                        </AppLayout>
                      </ProtectedRoute>
                    } />

                    <Route path="/export" element={
                      <ProtectedRoute>
                        <AppLayout>
                          <ExportOrders />
                        </AppLayout>
                      </ProtectedRoute>
                    } />

                    <Route path="/configuration" element={
                      <ProtectedRoute>
                        <AppLayout>
                          <Configuration />
                        </AppLayout>
                      </ProtectedRoute>
                    } />

                    <Route path="/history" element={
                      <ProtectedRoute>
                        <AppLayout>
                          <History />
                        </AppLayout>
                      </ProtectedRoute>
                    } />

                    <Route path="/sync" element={
                      <ProtectedRoute>
                        <AppLayout>
                          <SyncDashboard />
                        </AppLayout>
                      </ProtectedRoute>
                    } />

                    <Route path="/billing" element={
                      <ProtectedRoute>
                        <AppLayout>
                          <Billing />
                        </AppLayout>
                      </ProtectedRoute>
                    } />

                    {/* Fallback to landing */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </div>
              </Router>
            </ConfigProvider>
          </AuthProvider>
        </ShopifyProviders>
      </AppProvider>
    </QueryClientProvider>
  );
}

export default App;
